import * as Yup from "yup";
import { TBitValue, TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import { CREATE_CREDIT_PLAN, UPDATE_CREDIT_PLAN } from "../../services/Endpoints";
import { ICreditPlanPayload } from "../../services/credit-plan/CreditPlanService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import { requiredNumberRangeSchema } from "../../utils/PurchaseValidation";
import {
  CREDIT_BALANCE_MAX,
  PRICE_MAX,
  PURCHASE_NAME_MAX_LENGTH,
  TAX_PERCENT_MAX,
  TWO_DECIMALS_REGEX,
} from "../../utils/PurchaseConstants";
import { CREDIT_TYPE_ENUM, VISIBILITY_TYPE_ENUM } from "../../utils/PurchaseEnumerations";
import { ICreditPlan } from "./CreditPlanViewController";

export interface ICreditPlanForm {
  name: string;
  credit_type: CREDIT_TYPE_ENUM | null;
  credits: number | null;
  visibility_type: string;
  base_price: number | null;
  cgst: number | null;
  sgst: number | null;
  igst: number | null;
  is_active: TBitValue;
}

const CREDIT_PLAN_INITIAL_VALUES: ICreditPlanForm = {
  name: "",
  credit_type: null,
  credits: null,
  visibility_type: VISIBILITY_TYPE_ENUM.Public,
  base_price: null,
  cgst: 9,
  sgst: 9,
  igst: 18,
  is_active: BIT_VALUE.One,
};

const requiredMessage = (t: TUseTranslationTfn, fieldName: string) =>
  prepareMessageFromParams(t("error_message_required"), [["fieldName", fieldName]]);

const twoDecimalSchema = (t: TUseTranslationTfn, fieldName: string, max: number) =>
  requiredNumberRangeSchema(t, { fieldName, min: 0, max })
    .test("two-decimals", t("price_invalid_amount"), (value) =>
      value === undefined ? true : TWO_DECIMALS_REGEX.test(String(value)),
    );

const toPayload = (values: ICreditPlanForm, creditType: CREDIT_TYPE_ENUM): ICreditPlanPayload => ({
  name: values.name.trim(),
  credit_type: creditType,
  credits: Number(values.credits),
  visibility_type: values.visibility_type,
  base_price: Number(values.base_price),
  cgst: Number(values.cgst),
  sgst: Number(values.sgst),
  igst: Number(values.igst),
  is_active: values.is_active,
});

export const getCreditPlanInitialValues = (plan?: ICreditPlan): ICreditPlanForm =>
  plan
    ? {
        name: plan.name,
        credit_type: plan.credit_type,
        credits: Number(plan.credits),
        visibility_type: plan.visibility_type,
        base_price: Number(plan.base_price),
        cgst: Number(plan.cgst),
        sgst: Number(plan.sgst),
        igst: Number(plan.igst),
        is_active: plan.is_active,
      }
    : CREDIT_PLAN_INITIAL_VALUES;

export const creditPlanValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(requiredMessage(t, t("name")))
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("name")],
          ["min", "2"],
        ]),
      )
      .max(
        PURCHASE_NAME_MAX_LENGTH,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("name")],
          ["max", String(PURCHASE_NAME_MAX_LENGTH)],
        ]),
      ),
    credit_type: Yup.string().required(requiredMessage(t, t("credit_type"))),
    credits: requiredNumberRangeSchema(t, {
      fieldName: t("credits"),
      min: 1,
      max: CREDIT_BALANCE_MAX,
    }).integer(
      prepareMessageFromParams(t("error_message_integer"), [["fieldName", t("credits")]]),
    ),
    visibility_type: Yup.string().required(requiredMessage(t, t("type"))),
    base_price: twoDecimalSchema(t, t("base_price"), PRICE_MAX),
    cgst: twoDecimalSchema(t, t("cgst_percentage"), TAX_PERCENT_MAX),
    sgst: twoDecimalSchema(t, t("sgst_percentage"), TAX_PERCENT_MAX),
    igst: twoDecimalSchema(t, t("igst_percentage"), TAX_PERCENT_MAX),
  });

export const handleSubmitCreditPlan = async (
  values: ICreditPlanForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void,
  id: string | undefined,
) => {
  if (!values.credit_type) return;
  setLoading(true);
  try {
    const payload = toPayload(values, values.credit_type);
    const response = id
      ? await UPDATE_CREDIT_PLAN(payload, id)
      : await CREATE_CREDIT_PLAN(payload);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      handleClose(true);
      return;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "handleSubmitCreditPlan");
  } finally {
    setLoading(false);
  }
};
